import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminLayout from "../../masterLayout/AdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import SupportIssueChat from "../../components/SupportIssueChat";
import { adminApi, handleAdminLogoutIfNeeded } from "../../utils/adminApi";
import { connectSupportSocket } from "../../utils/supportWs";
import "../../components/SupportPingLayer.css";

const AdminPings = () => {
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi({ url: "support/admin/issues", method: "GET" });
      if (handleAdminLogoutIfNeeded(res)) return;
      if (res.success) {
        const list = Array.isArray(res.data) ? res.data : [];
        setIssues(list);
        setSelected((prev) => {
          if (!prev) return list[0] || null;
          return list.find((i) => String(i.id) === String(prev.id)) || prev;
        });
      } else {
        setIssues([]);
        setMsg(res?.msg || "Could not load support issues");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Admin Support Chat";
    load().catch(console.error);
    return connectSupportSocket({ role: "admin" });
  }, [load]);

  useEffect(() => {
    const onWs = (e) => {
      const d = e.detail;
      if (!d?.issue) return;
      setIssues((list) => {
        const base = Array.isArray(list) ? list : [];
        const existing = base.find((i) => String(i.id) === String(d.issue.id));
        const rest = base.filter((i) => String(i.id) !== String(d.issue.id));
        return [{ ...existing, ...d.issue }, ...rest];
      });
      setSelected((prev) =>
        prev && String(prev.id) === String(d.issue.id) ? { ...prev, ...d.issue } : prev
      );
    };
    window.addEventListener("support-ws", onWs);
    return () => window.removeEventListener("support-ws", onWs);
  }, []);

  const setStatus = async (status) => {
    if (!selected?.id) return;
    const res = await adminApi({
      url: `support/admin/issues/${selected.id}/status`,
      method: "POST",
      body: { status },
    });
    if (handleAdminLogoutIfNeeded(res)) return;
    setMsg(res.msg || (res.success ? "Updated" : "Failed"));
    if (res.success && res.data) {
      setSelected(res.data);
      setIssues((list) => {
        const base = Array.isArray(list) ? list : [];
        const rest = base.filter((i) => String(i.id) !== String(res.data.id));
        return [res.data, ...rest];
      });
    }
  };

  const issueList = Array.isArray(issues) ? issues : [];
  const pending = issueList.filter((i) => i.status !== "closed").length;

  return (
    <AdminLayout>
      <div className="admin-ui">
        <AdminPageHeader
          title="Support chat"
          subtitle={`${issueList.length} issues · ${pending} open. Live chat with users on each ticket.`}
        />
        {msg ? <div className="admin-alert admin-alert-info">{msg}</div> : null}

        <div className="row g-3">
          <div className="col-lg-4">
            <div className="card support-ping-card">
              <div className="card-body p-0">
                <div className="support-issue-list-head">Issues</div>
                {loading ? (
                  <p className="p-3 mb-0">Loading…</p>
                ) : issueList.length === 0 ? (
                  <div className="support-ping-empty">
                    <Icon icon="mdi:inbox-outline" width={36} />
                    <p>No support issues yet.</p>
                  </div>
                ) : (
                  <ul className="support-issue-list">
                    {issueList.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          className={`support-issue-item${
                            selected && String(selected.id) === String(item.id) ? " active" : ""
                          }`}
                          onClick={() => setSelected(item)}
                        >
                          <div className="d-flex justify-content-between gap-2">
                            <strong>{item.subject}</strong>
                            {Number(item.unread_admin) > 0 ? (
                              <span className="support-unread">{item.unread_admin}</span>
                            ) : null}
                          </div>
                          <span className="support-issue-preview">
                            {item.user_name || item.user_email || item.uid}
                          </span>
                          <span className="support-issue-preview">{item.last_message || "—"}</span>
                          <span className={`support-ping-status ${item.status === "closed" ? "" : "ok"}`}>
                            {item.status}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            {selected ? (
              <div className="d-flex flex-wrap gap-2 mb-2">
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  style={{ minHeight: 34 }}
                  onClick={() => setStatus("open")}
                >
                  Mark open
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-ghost"
                  style={{ minHeight: 34 }}
                  onClick={() => setStatus("answered")}
                >
                  Mark answered
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-danger"
                  style={{ minHeight: 34 }}
                  onClick={() => setStatus("closed")}
                >
                  Close issue
                </button>
              </div>
            ) : null}
            <SupportIssueChat
              role="admin"
              apiBase="support/admin"
              issue={selected}
              onIssueUpdated={(issue) => {
                setSelected((prev) =>
                  prev && String(prev.id) === String(issue.id) ? { ...prev, ...issue } : prev
                );
                setIssues((list) => {
                  const existing = list.find((i) => String(i.id) === String(issue.id));
                  const rest = list.filter((i) => String(i.id) !== String(issue.id));
                  return [{ ...existing, ...issue }, ...rest];
                });
              }}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPings;
